import { useId, useMemo } from "react";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { applyFilters } from "@/lib/filters/apply.ts";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { QUALITY_MAX, QUALITY_MIN } from "@/lib/filters/types.ts";
import { setFilters, useFilters } from "@/state/filters.ts";
import styles from "./controls.module.css";

/**
 * The quality floor — the axis the original site doesn't have.
 *
 * Quality never feeds the tier, so this gate can only ever *subtract*, and by
 * default it isn't allowed to subtract an essential. That exemption is the
 * toggle here rather than a hidden constant, because the honest version of
 * "raise the bar" is "…but you still have to watch The Marvels".
 */
/** The filter still stores 0-100; only the label speaks stars. */
const toStars = (score: number) => {
  const stars = Math.round((score / 20) * 2) / 2;
  return Number.isInteger(stars) ? String(stars) : stars.toFixed(1);
};

export function QualityGate() {
  const catalog = useCatalog();
  const filters = useFilters();
  const { entries } = useFilteredRoad();
  const sliderId = useId();

  // What the gate specifically is costing you: the same filters with the floor
  // dropped, minus what's on screen now.
  const cut = useMemo(() => {
    if (filters.qualityMin === QUALITY_MIN) return 0;
    return (
      applyFilters(catalog, { ...filters, qualityMin: QUALITY_MIN }).entries.length - entries.length
    );
  }, [catalog, filters, entries.length]);

  const rescued = useMemo(() => {
    if (filters.qualityMin === QUALITY_MIN || !filters.essentialsIgnoreQuality) return 0;
    return catalog.entries.filter(
      (e) =>
        e.curation.tier === "essential" &&
        // Unrated essentials are not "rescued" by the exemption — the floor
        // was never going to cut them, so counting them would overstate it.
        e.quality.score !== null &&
        e.quality.score < filters.qualityMin,
    ).length;
  }, [catalog, filters.qualityMin, filters.essentialsIgnoreQuality]);

  return (
    <div className={styles.field}>
      <div className={styles.head}>
        <label className={styles.label} htmlFor={sliderId}>
          Quality floor
        </label>
        <output className={styles.value} htmlFor={sliderId}>
          {filters.qualityMin === QUALITY_MIN ? "Off" : `${toStars(filters.qualityMin)}★ and up`}
        </output>
      </div>
      <input
        id={sliderId}
        className={styles.range}
        type="range"
        min={QUALITY_MIN}
        max={QUALITY_MAX}
        step={5}
        value={filters.qualityMin}
        aria-valuetext={
          filters.qualityMin === QUALITY_MIN
            ? "off"
            : `${toStars(filters.qualityMin)} stars and above`
        }
        onChange={(event) =>
          setFilters((prev) => ({ ...prev, qualityMin: Number(event.target.value) }))
        }
      />
      <label className={styles.switch}>
        <input
          type="checkbox"
          checked={filters.essentialsIgnoreQuality}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, essentialsIgnoreQuality: event.target.checked }))
          }
        />
        <span>Ignore quality for essentials</span>
      </label>
      <p className={styles.hint}>
        Quality is a separate axis from importance. An essential film you don't love is still
        essential.
        {cut > 0 && ` The gate is cutting ${cut} ${cut === 1 ? "stop" : "stops"}`}
        {cut > 0 && rescued > 0
          ? `, and keeping ${rescued} essential ${rescued === 1 ? "one" : "ones"} anyway.`
          : cut > 0
            ? "."
            : ""}
      </p>
    </div>
  );
}
